/* eslint-disable no-underscore-dangle */
import {
  useEffect, useState, memo, useCallback, useRef,
} from 'react';
import { useFragment, graphql } from 'react-relay';
import { isMobile } from 'react-device-detect';
import CarouselEmbla from '@/components/core/carousel-embla';
import Icon from '@/core/icon.js';
import { lang, useTranslation } from '@/helpers/translate';
import SpaceIconPopover from './space/iconpopover.js';
import Emitter from '@/helpers/emitter.js';
import Bookmark from '@/components/space/public/static/bookmark.js';
import { ORImageSpace as Image } from '@/core/image';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import LocalStorage from '@/helpers/localStorage';
import PriceByPeriod from './space/priceByPeriod';
import { useZusHomeStore } from '@/helpers/context';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import HofPlusIcon from '@/components/icons/hofplus';
import computeType from '@/helpers/space/computeType.js';

const graphqlSpaceResult = graphql`
  fragment spaceResultSearchFragment on Space @argumentDefinitions(
  disposition: {type: "[String]"},
  datesSelected: {type: "[JSONObject]"}
  lang: {type: "String!"}
  riders: {type: "Int"}
  ) {
    spaceId
    ref
    title(lang: $lang)
    surface
    rate
    type
    searchMap {
      location
      slug
      place_id
    }
    dataByCategory(category: "production") {
      hourlyRateExclTax
      price {
        amount
        amount_excl_tax
      }
      corpoPrice: price(input: {byViewer : true}) {
        amount
        amount_excl_tax
      }
      # WEA-157 — % Legend+ du contexte de recherche (15 / 30 / 50), pour le badge -X%.
      # Le taux applique au prix DU VIEWER reste disponible via
      # appliedDiscountPercent(input: {byViewer : true}) — a aliaser si on le rajoute.
      appliedDiscountPercent
      capacities {
        value : maxRidersByParams(category: "production", disposition: $disposition)
      }
      insta
    }
    pictures: searchElasPictures(count:6) {
      nameId
      extension
      predictName
    }
    urlSpacefr: urlByLang(lang: "fr")
    urlSpaceen: urlByLang(lang: "en")
    urlSpacees: urlByLang(lang: "es")
    reviewCounter
    selection
    badge
    technicalSheet {
      isPublic
    }
    fidelityProgram
    isSuperhost: hasTags(tags:["SUPERHOST"])
    covid_free
    searchSimuPrice(category: "production", dates: $datesSelected, riders: $riders)
    forceGeneratePicture
  }
`;

function SpaceResult({
 item, isBookmark = true, lcpPriority, ...props
}) {
  const currentRefDiv = useRef();
  const langCurrent = lang();
  const { t } = useTranslation();
  const { datesSelected, datePicked } = useSearchZustandStore.getState();
  const userData = useZusUserConnectedStore((state) => state.userData);
  const [isHover, setIsHover] = useState(null);
  const { ua } = useZusHomeStore.getState();
  const JSActive = typeof window !== 'undefined';
  const [currentIndex, setCurrentIndex] = useState(0);
  // target : on part de la valeur du rendu serveur ('_self') et on passe à '_blank' APRÈS le
  // montage. Avant, l'attribut était calculé sur la présence de window, donc différent entre
  // serveur et client ("Prop target did not match") — invisible jusqu'ici parce que React jetait
  // le HTML serveur de cette colonne (cf. correctif d'hydratation dans components/pages/search.js).
  const [linkTarget, setLinkTarget] = useState('_self');
  useEffect(() => {
    setLinkTarget('_blank');
  }, []);

  const handleIndexChange = (index) => {
    setCurrentIndex(index);
  };

  function onmousehover(value) {
    setIsHover(value);
    Emitter.emit(`searchresultsspacehoversignalemittospace${item.ref}`, value);
    Emitter.emit('searchresultsspacehoversignalemitcluster', { ref: item.ref, value });
    if (item?.searchMap?.slug) {
      Emitter.emit('searchresultsspacehoversignalemittopolygons', { slug: item?.searchMap?.slug, isHover: value });
    }
    if (item?.searchMap?.place_id) Emitter.emit(`searchresultsspacehoversignalemittocity${item?.searchMap?.place_id}`, value);
  }

  // WEA-157 — quand on vient de la search avec le filtre "Legend +" actif, on propage l'intention
  // via `?hof=1` sur le lien vers l'espace : la fiche espace pré-active alors l'opt-in Legend+ (cf.
  // components/space/public/cartridge/legend.js). Uniquement "Legend +" (pas le filtre HOF palier).
  const appendHofFlag = useCallback((url) => {
    if (!url) return url;
    if (useSearchZustandStore.getState().hofFilter !== 'legendplus') return url;
    return `${url}${url.includes('?') ? '&' : '?'}hof=1`;
  }, []);
  const getUrl = useCallback(
    () => appendHofFlag(item?.[`urlSpace${langCurrent}`]),
    [langCurrent, item, appendHofFlag],
  );

  const getPriceDefault = useCallback(() => {
    const priceByDefault = item?.dataByCategory?.price?.amount_excl_tax || item?.dataByCategory?.price?.amount;
    const price = item?.dataByCategory?.corpoPrice?.amount_excl_tax || priceByDefault;
    return { priceByDefault, price };
  }, [item?.dataByCategory]);

  const isSamePrice = useCallback(() => {
    const { priceByDefault, price } = getPriceDefault();
    return priceByDefault === price;
  }, [item?.dataByCategory]);
  const hasHofBadge = !!item?.fidelityProgram || !isSamePrice();
  // WEA-157 Legend: an active HOF subscription shows the Legend tier by default (effective level).
  const viewerFidelityLevel = userData?.corpo?.effectiveFidelityLevel ?? userData?.corpo?.fidelityLevel;
  const isViewerHofMember = !!viewerFidelityLevel;

  // WEA-157 — ruban de remise HOF sur la card (espace HOF uniquement) : juste "-X%" (sans mot "HOF"),
  // avec une étoile SEULE, ou une étoile + "+" rouge (HofPlusIcon plus) quand l'espace bénéficie du
  // taux HOF+ (remise legend > 15%, ex. −50%) pour distinguer HOF de HOF+.
  //   'legendplus' -> X = appliedDiscountPercent (15 ou 50) ; "+" rouge si > 15.
  //   'tier'/membre -> X = palier du viewer (5/10/15), étoile seule.
  const hofFilter = useSearchZustandStore((state) => state.hofFilter);
  const isHofSpace = !!item?.fidelityProgram;
  const appliedPct = item?.dataByCategory?.appliedDiscountPercent;
  let hofBadgePercent = null;
  // Sous le filtre "Legend +", TOUS les badges portent l'étoile avec "+" (contexte HOF+).
  const hofBadgePlus = hofFilter === 'legendplus';
  if (isHofSpace) {
    if (hofFilter === 'legendplus') {
      hofBadgePercent = Number(appliedPct) || null;
    } else if (hofFilter === 'tier' || isViewerHofMember) {
      hofBadgePercent = Number(viewerFidelityLevel) || null;
    }
  }
  const showTopHofBadge = hofBadgePercent != null && hofBadgePercent > 0;

  function getUrlSSR() {
    return `${item?.[`urlSpace${langCurrent}`]}`;
  }

  /*
    WEA-163 - report de la recherche vers la fiche : en mode horaire, on transmet aussi la
    DUREE voulue par date, pour que la cartouche propose d'emblee un creneau tenant dans
    les tranches encore libres. Sans elle, les dates arrivaient muettes et le client devait
    ouvrir chaque date pour saisir des heures.
  */
  function handOffDatesToSpace() {
    LocalStorage.set(`calendar_datespicked${item.ref}`, JSON.stringify(datePicked));
    LocalStorage.set(`calendar_period${item.ref}`, JSON.stringify(datesSelected));
    /*
      Les creneaux souhaites viennent du store et non de `varGraphql` : le creneau ne part pas
      au serveur, seule sa duree y va. `{ hours, start, end }` permet a la fiche de proposer
      d'abord le creneau demande, et de se rabattre sur une tranche libre s'il est pris.
    */
    const hoursWanted = useSearchZustandStore.getState()?.hourlySlots || {};
    // Selection MIXTE : seules les dates portant une duree partent a l'heure, les autres
    // gardent leur periode. On ne se fie donc pas au chip mais aux dates elles-memes.
    if (!Object.keys(hoursWanted).length) {
      LocalStorage.remove(`calendar_hours${item.ref}`);
      return;
    }
    LocalStorage.set(`calendar_hours${item.ref}`, JSON.stringify(hoursWanted));
  }

  function getPrice() {
    if (item?.searchSimuPrice) {
      if (item.searchSimuPrice?.syntheticPerDay) {
        const perDay = Number(item.searchSimuPrice?.priceByDates?.excl_tax);
        const beforeFidelity = Number(item.searchSimuPrice?.priceByDatesBeforeFidelity?.excl_tax);
        const text = t('space:formules.day');
        return (
          <div className="flex flex-row items-center">
            {Number.isFinite(beforeFidelity) && Number.isFinite(perDay) && beforeFidelity > perDay && (
              <span className="block text-gray-400 line-through mr-1 text-xs">
                {beforeFidelity}
                €
              </span>
            )}
            <span className="block font-medium">
              {perDay}
              €
            </span>
            <span className="ml-1 text-purple-5/50">
              par
              {' '}
              {text}
            </span>
          </div>
        );
      }
      return <PriceByPeriod details={item?.searchSimuPrice} spaceRef={item.ref} />;
    }
    // eslint-disable-next-line max-len
    const { priceByDefault, price } = getPriceDefault();
    const text = t('space:formules.day');
    return (
      <div className="flex flex-row items-center">
        {!isSamePrice() && (
        <span className="block text-gray-400 line-through mr-1 text-xs">
          {priceByDefault}
          €
        </span>
        )}
        <span className="block font-medium">
          {price}
          €
        </span>
        <span className="ml-1 text-purple-5/50">
          par
          {' '}
          {text}
        </span>
      </div>
    );
  }

  useEffect(() => {
    /*
      MI-907 scroll to the card space id instanly because that selected earlier
    */
    if (currentRefDiv?.current && isMobile && LocalStorage.get('selectedSpace') && LocalStorage.get('selectedSpace') === item.ref) {
      LocalStorage.set('selectedSpace', null);
      setTimeout(() => {
        currentRefDiv?.current?.scrollIntoView?.({ behavior: 'instant' });
        window.scrollBy(0, -96);
      }, 0);
    }
  }, [currentRefDiv]);

  useEffect(() => {
    if (JSActive) {
      // Mettre à jour le lien lorsque le JavaScript est actif
      const link = document.getElementById('active-link');
      link.href = getUrl();
    }
  }, [JSActive, langCurrent]);

  if (!item) {
    return null;
  }

  const getPictures = () => {
    if ('picturesOriginals' in props) {
      return props.picturesOriginals;
    }
    return item?.pictures;
  };

  return (
    <div
      className={`transition cursor-pointer relative bg-white hover:bg-white prev-next ${isHover ? 'prev-next-hover' : ''}`}
      onMouseEnter={() => { onmousehover(!!item.ref); }}
      onMouseLeave={() => { onmousehover(false); }}
      id={`search-results-space-${item?.ref}`}
      ref={currentRefDiv}
    >
      <div className="">
        <div className="col-span-3 h-80 lg:h-[20vw] rounded-[10px] overflow-hidden group relative bg-purple-5/10">
          {(!item.badge && !showTopHofBadge && !item?.technicalSheet?.isPublic) || (
            <div className="absolute z-10 mt-4 ml-4 flex items-center gap-2">
              {!item?.technicalSheet?.isPublic || (
                <div className="tracking-wide rounded px-2 py-[3px] bg-green flex items-center">
                  <span className="text-xs text-white">{t('space:badge.technicalSheet')}</span>
                </div>
              )}
              {!item.badge || (
                <div className={`tracking-wide rounded px-2 py-[3px] ${item.badge === 'new' ? 'bg-white flex items-center ' : 'bg-white'}`}>
                  {item.badge === 'new' ? <Icon name="shine" className="w-4 mr-1 text-dark" /> : ''}
                  <span className={`text-xs ${item.badge === 'new' ? 'text-black' : 'text-black'}`}>
                    {t(`space:badge.${item.badge}`)}
                  </span>
                </div>
              )}
              {!showTopHofBadge || (
                <div className="tracking-wide rounded px-2 py-[3px] bg-purple flex items-center">
                  <span className="mr-1 flex items-center text-white">
                    <HofPlusIcon className="size-4" plus={hofBadgePlus} />
                  </span>
                  <span className="text-xs text-white whitespace-nowrap">
                    {`-${hofBadgePercent}%`}
                  </span>
                </div>
              )}
            </div>
          )}
          {isBookmark && <div className="absolute z-40 px-2 py-2 rounded-lg right-3 groupe top-3">{!item.spaceId || <Bookmark className="cursor-pointer size-5" spaceId={item.spaceId} />}</div>}
          <CarouselEmbla
            slidesToScroll={1}
            loop
            className="h-full rounded-[10px] overflow-hidden relative z-5"
            onIndexChange={handleIndexChange}
          >
            {(getPictures() || []).map((picture, index) => (
              <div
                className="h-full embla__slide__search"
                key={`searchresultsspacepicture${picture?.__id || index}`}
                onClick={() => {
                  handOffDatesToSpace();
                  if (isMobile) {
                    LocalStorage.set('selectedSpace', item.ref);
                    window.open(getUrl(), '_self');
                  } else {
                    window.open(getUrl(), '_blank');
                  }
                }}
              >
                <div className="h-full embla__slide__inner">
                  <Image
                    className="h-full cursor-pointer carousel-cell-search bg-purple-0"
                    cardsSpaces
                    picture={picture}
                    // 1re photo des cartes au-dessus de la ligne de flottaison : chargement
                    // prioritaire (eager + fetchpriority high) — c'est le LCP de la page.
                    // Les photos 2+ du carrousel et les cartes suivantes restent lazy.
                    priority={!!(lcpPriority && index === 0)}
                    hdMode={index === 0}
                    rootMargin="10% 10%"
                    options={{
                      alt: item.title,
                      index,
                      currentIndex,
                      forceGeneratePicture: item?.forceGeneratePicture,
                    }}
                    search
                    defaultAlt={`${t('search:filter.space')} ${item.title} - ${index}`}
                  />
                </div>
              </div>
            ))}
          </CarouselEmbla>
        </div>
        <div className="relative h-full py-2 ml-0 sm:col-span-2">
          <a
            id="active-link"
            target={linkTarget}
            href={getUrlSSR()}
            onClick={() => {
              handOffDatesToSpace();
              if (isMobile) {
                window.open(getUrl(), '_self');
              } else {
                window.open(getUrl(), '_blank');
              }
            }}
            rel="noreferrer"
          >
            {!item.selection || <div className="absolute px-2 rounded bg-purple/10"><span className="text-sm text-purple">{t('space:badge.selection')}</span></div>}
            <div className="flex items-center h-full grow">
              <div className="w-full text-[15px] leading-6 rounded-b-lg">
                <div className="flex justify-between">
                  <div className="font-medium text-purple-6">{computeType({ type: item?.type, t })}</div>
                  {!item.rate ? <div className="static flex items-center">&nbsp;</div> : (
                    <div className="flex items-center">
                      <Icon className="text-black fill-current size-3" name="star" />
                      <span className="mx-1 text-[14px] font-medium text-purple-5">{item.rate}</span>
                      <span className="text-purple-5/50">
                        (
                        {item?.reviewCounter <= 1
                          ? t('review:count_review', {
                            count: item?.reviewCounter || 0,
                          })
                          : t('review:count_review_plural', {
                            count: item?.reviewCounter || 0,
                          })}
                        )
                      </span>
                    </div>
                  )}
                </div>
                <h2 className="mb-[3px] text-purple-5/50 text-sm mr-11 truncate overflow-ellipsis">{item.title}</h2>
                <div className="mt-[4px] items-center flex gap-x-6 text-sm">
                  {/* <div className="flex items-center">
                    <Icon className="w-4 h-4 mr-1 text-dark" name="man" />
                    {t('space:max_riders', { value: item?.dataByCategory?.capacities?.value })}
                  </div> */}
                  <div className="flex items-center">
                    <div className="flex flex-col">
                      {getPrice()}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Icon className="w-4 h-4 mr-1 fill-current text-purple-5/30" name="surface" />
                    <span className="ml-0.5 font-medium text-purple-5">
                      {item.surface}
                      <span className="ml-1 font-normal text-purple-5/50">m²</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-x-1">
                    {!item?.dataByCategory?.insta || <SpaceIconPopover name="flash" />}
                    {!item?.isSuperhost || <SpaceIconPopover name="superhost" />}
                    {hasHofBadge && <SpaceIconPopover name="hof2" />}
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
      {/* <a style={{ position: 'fixed', top: -10000, left: -10000 }} href={getUrlSSR()} target="_blank" rel="noreferrer">{item.title}</a> */}
    </div>
  );
}

export default function RootComponent({ item: itemData, ...props }) {
  const item = useFragment(graphqlSpaceResult, itemData);
  const propsBkPictures = {};
  if ('picturesOriginals' in itemData) {
    propsBkPictures.picturesOriginals = itemData?.picturesOriginals;
  }
  return <SpaceResult {...{ item }} {...props} {...propsBkPictures} />;
}
